import { appContainer } from '../contexts/app';
import { OrderedMap } from 'immutable';
import feathers from '@feathersjs/client';
import isEmail from 'validator/lib/isEmail';
import { isStrongPassword } from '../lib';
const { BadRequest } = feathers.errors;

// Wrap a hook declaration to make it skippable.
// Pass params 'skipHook' with either a string or array
// of strings that match `hookName`to skip those hooks.
// NOTE: you must wrap the HOOK, not a function that returns a hook...
export const skippable = (hookName, hookFunc) => {
  return context => {
    if (context.params.skipHooks) {
      const skipHooks = Array.isArray(context.params.skipHooks)
        ? context.params.skipHooks
        : [context.params.skipHooks];
      if (skipHooks.includes(hookName)) {
        return context;
      } else {
        return hookFunc(context);
      }
    } else {
      return hookFunc(context);
    }
  };
};

export const toImmutable = (Record, _id = '_id') => {
  return skippable('toImmutable', context => {
    if (context.method === 'find') {
      context.result.data = OrderedMap(
        context.result.data.map(res => [res[_id], new Record(res)])
      );
    } else {
      context.result = new Record(context.result);
    }
    return context;
  });
};

export const fromImmutable = skippable('fromImmutable', context => {
  if (context.data.toJS) {
    context.data = context.data.toJS();
  }
  if (context.method === 'create') {
    // Our record has to have a prop for _id and
    // by default it is a blank string. The server will
    // barf if trying to create with an _id already.
    delete context.data._id;
  }
  return context;
});

// set a whole list, used after .find() to update the whole list
// TODO: This should not just replace the whole list, it should
// loop the result and either overwrite or append to the list
export const setListInState = stateProp => {
  return skippable('setListInState', context => {
    appContainer.set(stateProp, context.result.data);
    return context;
  });
};

// Set a record in a list
export const setInState = stateProp => {
  return skippable('setInState', context => {
    appContainer.setIn([stateProp, context.result._id], context.result);
    return context;
  });
};

export const deleteInState = stateProp => {
  return skippable('deleteInState', context => {
    appContainer.deleteIn([stateProp, context.result._id]);
    return context;
  });
};

// Sometime when working on assets, we need to rehydrate some parent
// that asset is related to. Specifically this happens when we create/remove
// "ordered" things like floors/infographics. This action updates the
// respective "floors_order" and "infographics_order" on building/marker
// That updated building/marker needs to be hydrated in state.
export const hydrateParent = (serviceName, parentIdProp) => {
  return skippable('hydrateParent', async context => {
    const service = context.app.service(serviceName);
    const { result } = context;
    const parentId = result[parentIdProp];
    // Note we do not await this...we don't really need to?
    // This service call's hooks will actually place it in proper
    // place in state.
    service.get(parentId);
    return context;
  });
};

export const validate = validationSchema => {
  return context => {
    Object.keys(validationSchema).forEach(prop => {
      // Create a ref to the object by allowing the use of dot notation in the function
      const dataValue = prop.split('.').reduce((obj, value) => {
        return obj[value];
      }, context.data);
      if (typeof validationSchema[prop] === 'function') {
        validationSchema[prop](dataValue, context.data);
      } else if (!dataValue) {
        throw new BadRequest(validationSchema[prop]);
      }
    });
    return context;
  };
};

// Note this is not a hook, but a func to be used in
// tandem with the validate hook
export const validateEmail = email => {
  if (!email) {
    throw new BadRequest('Email is required.');
  }
  if (!isEmail(email)) {
    throw new BadRequest('Please enter a valid email address.');
  }
};

// Note this is not a hook, but a func to be used in
// tandem with the validate hook
export const validatePassword = password => {
  if (!password) {
    throw new BadRequest('Password is required.');
  }
  if (!isStrongPassword(password)) {
    throw new BadRequest('Please use a stronger password.');
  }
};



// WEBPACK FOOTER //
// ./src/feathers/hooks.js